import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

/**
 * The record a page shows, for the page title (design-system §9: on a record page the record's name
 * comes first once it has loaded, so a user with several tabs open can tell them apart).
 *
 * A route's `handle.title` names the screen and is fixed; a record's name is known only once its read
 * lands, so the screen publishes it here and `RouteTitle` (`src/app`) puts it first. Nothing else sets
 * the title: a feature never renders its own `<title>`.
 */
interface RecordTitleValue {
    readonly title: string | undefined;
    readonly setTitle: (title: string | undefined) => void;
}

const RecordTitleContext = createContext<RecordTitleValue>({
    title: undefined,
    setTitle: () => undefined,
});

/** Holds the current record's name for `RouteTitle`. Mounted once, around the router (`main.tsx`). */
export function RecordTitleProvider({ children }: { readonly children: ReactNode }) {
    const [title, setTitle] = useState<string | undefined>(undefined);
    const value = useMemo(() => ({ title, setTitle }), [title]);
    return <RecordTitleContext.Provider value={value}>{children}</RecordTitleContext.Provider>;
}

/**
 * Publish the loaded record's name for the page title while this component is mounted. Pass
 * `undefined` until the name is known; the title falls back to the route's own then. Unmounting
 * clears it, so a name never outlives its page.
 */
export function useRecordTitle(name: string | undefined): void {
    const { setTitle } = useContext(RecordTitleContext);
    useEffect(() => {
        const trimmed = name?.trim();
        setTitle(trimmed === '' ? undefined : trimmed);
        return () => setTitle(undefined);
    }, [name, setTitle]);
}

/** The record name currently published, or `undefined` — read by `RouteTitle`. */
export function useCurrentRecordTitle(): string | undefined {
    return useContext(RecordTitleContext).title;
}
